import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth, API } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { LanguageToggle } from "@/components/LanguageToggle";
import { SignOut } from "@phosphor-icons/react";

const LOGO = "/logo-servitech.png";

export const AppHeader = () => {
  const { user, logout } = useAuth();
  const { t, lang } = useLang();
  const [liveCount, setLiveCount] = useState(0);
  useEffect(() => {
    if (user?.role !== "admin") return;
    const beep = () => {
      try {
        const ctx = new (window.AudioContext || window.webkitAudioContext)();
        [880, 1320].forEach((f, i) => {
          const o = ctx.createOscillator(); const g = ctx.createGain();
          o.frequency.value = f; o.connect(g); g.connect(ctx.destination);
          g.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.18); g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.16);
          o.start(ctx.currentTime + i * 0.18); o.stop(ctx.currentTime + i * 0.18 + 0.17);
        });
      } catch (e) { /* audio unavailable */ }
    };
    const poll = () => axios.get(`${API}/admin/vendor-live/count`).then(({ data }) => {
      setLiveCount(data.new_orders);
      const seen = sessionStorage.getItem("vendor_live_latest");
      if (data.latest_at && seen && data.latest_at > seen) {
        beep();
        toast.success(lang === "en" ? "New vendor order received" : "Nouvelle commande d'un vendeur reçue", {
          action: { label: lang === "en" ? "View" : "Voir", onClick: () => navigate("/admin/vendor-live") },
        });
      }
      if (data.latest_at) sessionStorage.setItem("vendor_live_latest", data.latest_at);
    }).catch(() => {});
    poll();
    const iv = setInterval(poll, 20000);
    return () => clearInterval(iv);
  }, [user?.role, lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const tabs = [
    { label: t.dashboard, path: "/admin", match: (p) => p === "/admin" },
    { label: t.clients, path: "/admin/customers", match: (p) => p.startsWith("/admin/customers") },
    { label: t.orders, path: "/admin/orders", match: (p) => p.startsWith("/admin/orders") },
  ];
  if (user?.role === "admin") {
    tabs.push({ label: t.products, path: "/admin/products", match: (p) => p.startsWith("/admin/products") });
    tabs.push({ label: t.categories, path: "/admin/categories", match: (p) => p.startsWith("/admin/categories") });
    tabs.push({ label: t.website, path: "/admin/website", match: (p) => p.startsWith("/admin/website") });
    tabs.push({ label: t.team, path: "/admin/team", match: (p) => p.startsWith("/admin/team") });
    tabs.splice(3, 0, { label: lang === "en" ? "Live vendors" : "Vendeurs en direct", path: "/admin/vendor-live", match: (p) => p.startsWith("/admin/vendor-live"), badge: liveCount });
  }

  return (
    <header data-testid="app-header" className="sticky top-0 z-40">
      {/* Tier 1 — slate account bar (matches storefront) */}
      <div className="bg-[#2e3a47] text-white/80">
        <div className="flex items-center justify-between px-4 sm:px-6 py-1.5 text-xs">
          <div className="font-bold uppercase tracking-[0.25em] text-white/70">{t.backOffice}</div>
          <div className="flex items-center gap-4">
            <button data-testid="header-profile-link" onClick={() => navigate("/admin/profile")}
              className="flex items-center gap-2 transition-opacity duration-200 hover:opacity-80">
              <span className="hidden font-medium text-white sm:inline">{user?.name || user?.email}</span>
              <span className="bg-white/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white/70">{user?.role}</span>
            </button>
            <LanguageToggle />
            <button data-testid="logout-button" onClick={handleLogout}
              className="flex items-center gap-1.5 font-bold uppercase tracking-wider text-white/80 hover:text-white transition-colors duration-200">
              <SignOut size={14} weight="bold" />
              <span className="hidden sm:inline">{t.logout}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Tier 2 — white logo bar */}
      <div className="border-b border-border bg-white">
        <div className="flex items-center justify-between px-4 sm:px-6 py-3">
          <button data-testid="header-logo" onClick={() => navigate("/admin")} className="flex items-center gap-3">
            <img src={LOGO} alt="Servitech Xpert" className="h-11 w-auto object-contain" />
            <span className="hidden border-l border-border pl-3 text-[10px] font-bold uppercase tracking-[0.25em] text-muted-foreground md:inline">{t.backOffice}</span>
          </button>
        </div>
      </div>

      {/* Tier 3 — nav tabs */}
      <nav className="relative border-b border-border bg-white" data-testid="admin-nav">
        <div className="flex items-stretch gap-1 overflow-x-auto px-4 sm:px-6 scrollbar-hide">
          {tabs.map((tab) => {
            const active = tab.match(location.pathname);
            return (
              <button
                key={tab.path}
                data-testid={`nav-${tab.path}`}
                onClick={() => navigate(tab.path)}
                className={`whitespace-nowrap px-3 sm:px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors duration-200 border-b-2 ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground hover:bg-slate-50"}`}
              >
                {tab.label}
                {tab.badge > 0 && <span data-testid="vendor-live-badge" className="ml-1.5 rounded-full bg-red-600 px-1.5 py-0.5 text-[10px] font-bold text-white">{tab.badge}</span>}
              </button>
            );
          })}
        </div>
        <div className="pointer-events-none absolute inset-y-0 right-0 w-8 bg-gradient-to-l from-white to-transparent sm:hidden" aria-hidden="true" />
      </nav>
    </header>
  );
};
